import React, { useEffect, useState } from 'react';
import { Box, Button, Group, Paper, Progress, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconDownload, IconMap2, IconBuilding, IconFileAlert } from '@tabler/icons-react';
import { areaText, PROPERTY_TYPES, lookup } from '../../api/constantsRealEstate';
import { realEstateApi as api, errorMessage } from '../../api/apiRealEstate';
import { Empty, ErrorMessage, Loading, PageHead } from '../../components/RealEstate/Shared';

// Bu sayfa App.js üzerinden api veya refreshKey prop'u beklemez.
export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    api.report()
      .then((response) => {
        if (active) setData(response);
      })
      .catch((err) => {
        if (active) setError(errorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [nonce]);

  async function exportCsv() {
    setExporting(true);
    setError('');
    try {
      await api.exportProperties();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const total = Number(data?.total_properties || 0);
  const propertyTypes = data?.properties_by_type || [];
  const propertyCities = data?.properties_by_city || [];
  const percent = (count) => total > 0
    ? Math.max(0, Math.min(100, (100 * Number(count || 0)) / total))
    : 0;

  return (
    <>
      <PageHead
        eyebrow="Portföy analizi"
        title="Raporlar"
        description="Mülkiyet, lokasyon ve envanter özeti."
        action={
          <Button leftSection={<IconDownload size={17} />} loading={exporting} onClick={exportCsv}>
            CSV / Excel'e aktar
          </Button>
        }
      />
      <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />

      {loading ? <Loading /> : data && (
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="md">
            {[
              { title: 'Aktif gayrimenkuller', number: total, icon: IconBuilding },
              { title: 'Kayıtlı parsel alanı', number: areaText(data.total_unique_parcel_area_m2), icon: IconMap2 },
              { title: 'Evraksız mülkler', number: data.properties_without_documents ?? 0, icon: IconFileAlert },
            ].map(({ title, number, icon: Icon }) => (
              <Paper withBorder radius="lg" p="lg" key={title}>
                <Group gap="xs" c="dimmed">
                  <Icon size={19} />
                  <Text size="sm">{title}</Text>
                </Group>
                <Title order={3} mt="sm">{number}</Title>
              </Paper>
            ))}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder radius="lg" p="lg">
              <Title order={4} mb="md">Mülkiyet ve kullanım</Title>
              {[
                ['Şirkete ait', data.owned_properties, 'blue'],
                ['Kiralanan', data.rented_properties, 'cyan'],
                ['Boş', data.vacant_properties, 'orange'],
              ].map(([label, count, color]) => (
                <Box mb="md" key={label}>
                  <Group justify="space-between" mb={6}>
                    <Text size="sm">{label}</Text>
                    <Text size="sm" fw={600}>{count ?? 0}</Text>
                  </Group>
                  <Progress value={percent(count)} color={color} radius="xl" />
                </Box>
              ))}
            </Paper>

            <Paper withBorder radius="lg" p="lg">
              <Title order={4} mb="md">Mülk türleri</Title>
              {propertyTypes.length === 0 ? (
                <Empty title="Veri yok" />
              ) : propertyTypes.map((item) => (
                <Box mb="md" key={item.type}>
                  <Group justify="space-between" mb={6}>
                    <Text size="sm">{lookup(PROPERTY_TYPES, item.type)}</Text>
                    <Text size="sm" fw={600}>{item.count}</Text>
                  </Group>
                  <Progress value={percent(item.count)} color="indigo" radius="xl" />
                </Box>
              ))}
            </Paper>
          </SimpleGrid>

          <Paper withBorder radius="lg" p="lg">
            <Title order={4} mb="md">İllere göre dağılım</Title>
            {propertyCities.length === 0 ? (
              <Empty title="Veri yok" />
            ) : (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                {propertyCities.map((item, index) => (
                  <Box key={`${item.city || 'unknown'}-${index}`}>
                    <Group justify="space-between" mb={6}>
                      <Text size="sm">{item.city || 'Belirtilmemiş'}</Text>
                      <Text size="sm" fw={600}>{item.count}</Text>
                    </Group>
                    <Progress value={percent(item.count)} color="teal" radius="xl" />
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Paper>

          <Text c="dimmed" size="xs">
            İndirme, mevcut API'nin sunduğu gayrimenkul envanteri CSV çıktısıdır.
            PDF raporu ve tarihsel finans analizleri V1 backend kapsamında değildir.
          </Text>
        </Stack>
      )}
    </>
  );
}
